import { useCallback, useEffect, useRef, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X, Save, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { RuntimeMode } from '@/lib/canvas/shader/types'

export interface ShaderCodeModalProps {
  open: boolean
  nodeId: string | null
  initialCode: string
  runtimeMode: RuntimeMode
  title: string
  onSave: (nodeId: string, code: string) => void
  onClose: () => void
}

const INDENT = '  '

const PLACEHOLDERS: Record<RuntimeMode, string> = {
  glsl: '// @param float speed 0 5 1 Speed\n// Fragment shader: u_time, u_resolution, u_mouse, u_image are provided.',
  p5: '// @param float speed 0 5 1 Speed\n// p5.js global mode: write setup() and draw().',
  threejs: '// @param float speed 0 5 1 Speed\n// THREE is global; render into document.querySelector(\'canvas\').',
  raw: '// @param float speed 0 5 1 Speed\n// Plain JavaScript; draw into genie.canvas.',
}

/**
 * Fullscreen shader code editor.
 *
 * State is owned by the parent (CanvasPage) so this component stays free of
 * React Flow hooks (the modal is mounted outside ReactFlowProvider).
 */
export function ShaderCodeModal(props: ShaderCodeModalProps) {
  if (!props.open || !props.nodeId) return null
  return <ShaderCodeModalInner {...props} nodeId={props.nodeId} />
}

interface InnerProps extends Omit<ShaderCodeModalProps, 'nodeId'> {
  nodeId: string
}

function ShaderCodeModalInner({ nodeId, initialCode, runtimeMode, title, onSave, onClose }: InnerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState(initialCode)

  useEffect(() => { setDraft(initialCode) }, [nodeId, initialCode])

  useEffect(() => {
    requestAnimationFrame(() => textareaRef.current?.focus())
  }, [])

  const dirty = draft !== initialCode

  const handleSave = useCallback(() => {
    if (dirty) onSave(nodeId, draft)
    onClose()
  }, [dirty, nodeId, draft, onSave, onClose])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleSave])

  // Tab indents (Shift+Tab outdents) instead of leaving the editor. Esc still closes.
  const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab' || e.metaKey || e.ctrlKey || e.altKey) return
    e.preventDefault()
    const el = e.currentTarget
    const { value, selectionStart: start, selectionEnd: end } = el
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    if (!e.shiftKey && start === end) {
      el.setRangeText(INDENT, start, end, 'end')
    } else {
      const block = value.slice(lineStart, end)
      const lines = block.split('\n')
      const changed = e.shiftKey
        ? lines.map((line) => line.replace(/^ {1,2}|^\t/, ''))
        : lines.map((line) => INDENT + line)
      const firstDelta = changed[0].length - lines[0].length
      el.setRangeText(changed.join('\n'), lineStart, end, 'preserve')
      el.selectionStart = Math.max(lineStart, start + firstDelta)
      el.selectionEnd = lineStart + changed.join('\n').length
    }
    setDraft(el.value)
  }, [])

  const lineCount = draft.split('\n').length

  return (
    <DialogPrimitive.Root open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-2 z-50 flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-float duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-border px-4">
            <div className="flex items-center gap-3">
              <DialogPrimitive.Title className="font-mono text-xs font-medium uppercase tracking-wider">{title}</DialogPrimitive.Title>
              <span className="rounded bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">{runtimeMode}</span>
              {dirty && <span className="rounded bg-yellow-500/10 px-2 py-0.5 text-[10px] text-yellow-500">Unsaved</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setDraft(initialCode)} disabled={!dirty} className="h-7 gap-1.5">
                <RotateCcw className="h-3.5 w-3.5" />
                Revert
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!dirty} className="h-7 gap-1.5">
                <Save className="h-3.5 w-3.5" />
                Save
                <span className="ml-1 rounded bg-primary-foreground/10 px-1 py-0.5 font-mono text-[9px] text-primary-foreground/70">⌘S</span>
              </Button>
              <DialogPrimitive.Close className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Close editor">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="flex min-h-0 flex-1">
            <div
              ref={gutterRef}
              aria-hidden
              className="w-12 flex-shrink-0 select-none overflow-hidden border-r border-border bg-muted/40 px-2 py-2 text-right font-mono text-[11px] leading-5 text-muted-foreground"
            >
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onScroll={(e) => { if (gutterRef.current) gutterRef.current.scrollTop = e.currentTarget.scrollTop }}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              wrap="off"
              aria-label="Shader code"
              className="flex-1 resize-none bg-background px-3 py-2 font-mono text-[12px] leading-5 outline-none"
              placeholder={PLACEHOLDERS[runtimeMode]}
            />
          </div>

          <div className="flex flex-shrink-0 items-center justify-between border-t border-border px-4 py-1.5 text-[10px] text-muted-foreground">
            <span>{lineCount} lines · {draft.length.toLocaleString()} chars</span>
            <span>
              Expose a control with <code className="rounded bg-muted px-1 py-0.5 font-mono">// @param &lt;float|int&gt; name min max default [label]</code>,{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono">bool name true</code>,{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono">color name #ff00aa</code> or{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono">select name a,b,c a</code>.
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
